import Autocomplete, { AutocompleteProps } from '@material-ui/lab/Autocomplete';
import { DataFields } from 'api-clients/monolith';
import React, { useState, VFC } from 'react';

import { TextField, TextFieldProps } from '../TextField';
import { useStyles } from './styles';

// == Type  ========================================================================
export interface DropdownFieldProps extends TextFieldProps {
  id: string;
  options: Option[] | undefined;
  required: boolean;
  helperText?: React.ReactNode;
  handleOptionClick?: (item: Option | null) => void;
  previouslySelected?: Option;
  value?: Option;
  /**
   * Can completely override any other `Autocomplete` props
   */
  autocompleteProps?: Partial<AutocompleteProps<Option>>;
}

type Options = DataFields[number]['options'];
type Option = NonNullable<Options>[number] & { visible?: boolean };

// == Util ========================================================================
function initialSelection(
  visibleOptions: Option[] | undefined,
  previouslySelected: Option | undefined,
): Option | null {
  const restoredSelection = visibleOptions?.find(
    opt => opt.value === previouslySelected?.value,
  );
  return restoredSelection ?? visibleOptions?.find(opt => opt.default) ?? null;
}

// == Component ===================================================================
export const DropdownField: VFC<DropdownFieldProps> = ({
  autocompleteProps,
  id,
  options,
  helperText = 'Search Error',
  name,
  previouslySelected,
  handleOptionClick,
  value,
  ...rest
}) => {
  const classes = useStyles();

  const visibleOptions = options?.filter(option => {
    if (Object.prototype.hasOwnProperty.call(option, 'visible')) {
      return option.visible === true;
    } /* else -- no 'visible' property */

    return true;
  });

  // -- State ---------------------------------------------------------------------
  const [selected, setSelected] = useState<Option | null>(
    initialSelection(visibleOptions, previouslySelected),
  );

  // -- Handler -------------------------------------------------------------------
  const handleChange = (
    e: React.ChangeEvent<unknown>,
    value: Option | null,
  ) => {
    if (handleOptionClick) {
      handleOptionClick(value);
    }
    setSelected(value);
  };

  // -- UI ------------------------------------------------------------------------
  return (
    <>
      <Autocomplete
        id={`autocomplete_${id}`}
        fullWidth
        options={visibleOptions ?? []}
        getOptionLabel={opt => opt.label}
        value={value ?? selected}
        data-testid="autocomplete-search"
        className={classes.dropdown}
        onChange={(e, value) => handleChange(e, value)}
        renderInput={params => (
          <TextField {...params} {...rest} helperText={helperText} />
        )}
        {...autocompleteProps}
      />
      {/* Logic jumps look the question up by `id`, answered or not.
          Without a `name`, an unanswered dropdown submits nothing. */}
      <input
        type="hidden"
        id={id}
        value={selected?.value ?? ''}
        name={selected ? name : undefined}
      />
    </>
  );
};
